import { BigNumber } from "@ethersproject/bignumber";
import { useMemo } from "react";
import { QueryFunction, useQuery } from "@tanstack/react-query";
import { isNullOrUndefined, isUndefined, Nullable } from "@silo/utils";
import { useSiloWeb3 } from "./SiloWeb3Provider";
import { NATIVE_TOKEN, balanceOf } from "@silo/contract-utils";
import {
  ChainIdLegacy,
  ChecksumAddress,
  ClientProvider,
  isSupportedChainId,
} from "@silo/types";

export const useNativeToken = () => {
  const { chainId } = useSiloWeb3();

  return useMemo(() => {
    if (!isSupportedChainId(chainId)) return null;

    const { wrappedAddress, symbol, name, decimals } = NATIVE_TOKEN[chainId];

    return {
      address: wrappedAddress,
      symbol,
      name,
      decimals,
    };
  }, [chainId]);
};

type NativeTokenBalanceKey = [
  key: "nativeTokenBalance",
  clientProvider: Nullable<ClientProvider>,
  chainId: Nullable<ChainIdLegacy>,
  nativeTokenAddress: Nullable<ChecksumAddress>,
  account: Nullable<ChecksumAddress>
];

export const nativeTokenBalanceKey: NativeTokenBalanceKey[0] =
  "nativeTokenBalance";

const queryNativeTokenBalance: QueryFunction<
  BigNumber,
  NativeTokenBalanceKey
> = async ({ queryKey }) => {
  const [, clientProvider, chainId, nativeTokenAddress, account] = queryKey;

  if (
    isNullOrUndefined(clientProvider) ||
    isNullOrUndefined(chainId) ||
    !isSupportedChainId(chainId) ||
    isNullOrUndefined(nativeTokenAddress) ||
    isNullOrUndefined(account)
  ) {
    throw new Error();
  }

  return balanceOf(nativeTokenAddress, account, clientProvider, chainId);
};

export const useQueryNativeTokenBalance = () => {
  const { account, clientProvider, chainId } = useSiloWeb3();
  const nativeTokenAddress = useNativeToken()?.address;

  const { data } = useQuery(
    [
      "nativeTokenBalance",
      clientProvider,
      chainId,
      nativeTokenAddress,
      account,
    ],
    queryNativeTokenBalance,
    {
      enabled:
        !!clientProvider && !!chainId && !!nativeTokenAddress && !!account,
    }
  );

  return data;
};
